import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { InfoBanner } from "@/components/ui/banner";
import { StatCard } from "@/components/data-display/stat-card";
import { StateMachine } from "@/components/data-display/state-machine";
import { Table, TableWrapper } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { usePermission } from "@/hooks/use-permission";
import {
  DEP_SERVICES,
  DEP_ACCEPTANCE_CHECKS,
  DEP_HISTORY,
  DEP_OPERATE,
  DEP_TABLE,
} from "@/data/mock";
import type { DepServiceStatus } from "@/types";

export type DeploymentDetailStep = "bootstrap" | "acceptance" | "operate" | "history";

type StepId = "preflight" | DeploymentDetailStep;

const STEPS: { id: StepId; label: string }[] = [
  { id: "preflight", label: "Preflight" },
  { id: "bootstrap", label: "Service Bootstrap" },
  { id: "acceptance", label: "Acceptance Gate" },
  { id: "operate", label: "Operate" },
  { id: "history", label: "History" },
];

const SERVICE_BADGE: Record<DepServiceStatus, { variant: "success" | "info" | "warning"; label: string }> = {
  running: { variant: "success", label: "Running" },
  starting: { variant: "info", label: "Starting" },
  degraded: { variant: "warning", label: "Degraded" },
};

const SERVICE_DOT: Record<DepServiceStatus, string> = {
  running: "bg-success",
  starting: "bg-info",
  degraded: "bg-warning",
};

const SERVICE_PROGRESS_TONE: Record<DepServiceStatus, "success" | "info" | "warning"> = {
  running: "success",
  starting: "info",
  degraded: "warning",
};

const WRAP_IDs = ["vaultcore", "alloymesh", "reconflow"];

function StepBar({
  step,
  accepted,
  onSelect,
}: {
  step: StepId;
  accepted: boolean;
  onSelect: (s: DeploymentDetailStep) => void;
}) {
  return (
    <div className="overflow-x-auto border-b border-line bg-surface" role="tablist" aria-label="Deployment steps">
      {STEPS.map((s, i) => {
        const active = step === s.id;
        const complete = accepted && (s.id === "preflight" || s.id === "bootstrap" || s.id === "acceptance");
        const circle = (
          <span
            aria-hidden
            className={cn(
              "grid size-5 shrink-0 place-items-center rounded-full border text-[10px] font-extrabold transition-colors",
              active && !complete && "border-primary bg-primary text-on-primary",
              complete && "border-success bg-success text-white",
              !active && !complete && "border-line bg-bg text-muted"
            )}
          >
            {complete ? <Check className="size-3" /> : i + 1}
          </span>
        );
        const cls = cn(
          "flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-[13px] font-semibold",
          active ? "border-primary text-primary" : "border-transparent text-muted hover:text-ink"
        );

        if (s.id === "preflight") {
          return (
            <Link key={s.id} to="/deployment" className={cls}>
              {circle}
              {s.label}
            </Link>
          );
        }
        return (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={0}
            onClick={() => onSelect(s.id as DeploymentDetailStep)}
            className={cls}
          >
            {circle}
            {s.label}
          </button>
        );
      })}
    </div>
  );
}

export default function DeploymentDetail({ id, initialStep }: { id: string; initialStep: DeploymentDetailStep }) {
  const canManage = usePermission("admin:access");
  const deployment = DEP_TABLE.find((d) => d.id === id) ?? {
    id,
    customer: id,
    state: "Operating",
    badgeVariant: "success",
    lastUpdated: "—",
  };

  const [step, setStep] = useState<DeploymentDetailStep>(initialStep);
  const [accepted, setAccepted] = useState(false);
  const [bootstrapped, setBootstrapped] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (step !== "bootstrap") return;
    timerRef.current = window.setTimeout(() => setBootstrapped(true), 2200);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [step]);

  const services = bootstrapped
    ? DEP_SERVICES.map((s) =>
        WRAP_IDs.includes(s.id) ? { ...s, status: "running" as const, percent: 100 } : s
      )
    : DEP_SERVICES;
  const allRunning = services.every((s) => s.status === "running");

  const acceptDeployment = () => {
    setAccepted(true);
    setBootstrapped(true);
    setStep("operate");
  };

  const subtitle = (tag: string) => `${tag} · ${id} · ${deployment.customer}`;

  return (
    <div>
      <Breadcrumb items={[{ label: "Deployment", href: "/admin/deployment" }, { label: id }]} />

      <StepBar step={step} accepted={accepted} onSelect={setStep} />

      <div className="mt-5">
        {/* ── DEP-02 · Service Bootstrap ─────────────────────────────── */}
        {step === "bootstrap" && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex flex-col gap-1.5">
                <h1 className="text-2xl font-bold leading-[1.2] text-ink">Service Bootstrap</h1>
                <p className="text-xs text-muted">
                  {subtitle("DEP-02")} — starting all platform services. This typically takes 2–4 minutes.
                </p>
              </div>
              <Button disabled={!allRunning || !canManage} onClick={() => canManage && setStep("acceptance")}>
                Proceed to acceptance →
              </Button>
            </div>

            <div className="overflow-hidden rounded-[10px] border border-line bg-surface">
              <div className="flex items-center justify-between gap-3 border-b border-line bg-bg px-4 py-3.5">
                <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted">
                  Service catalog
                </div>
                <Badge variant={allRunning ? "success" : "info"}>
                  {allRunning ? "7/7 Running" : "Starting…"}
                </Badge>
              </div>
              {services.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center gap-3 border-b border-line px-4 py-3 last:border-b-0"
                >
                  <span aria-hidden className={cn("size-2.5 shrink-0 rounded-full", SERVICE_DOT[s.status])} />
                  <span className="min-w-0 flex-1 text-[13px] font-semibold text-ink">{s.name}</span>
                  <Badge size="sm" variant={SERVICE_BADGE[s.status].variant}>
                    {SERVICE_BADGE[s.status].label}
                  </Badge>
                  <Progress
                    value={s.percent}
                    tone={SERVICE_PROGRESS_TONE[s.status]}
                    size="sm"
                    showLabel={false}
                    className="w-[160px]"
                  />
                </div>
              ))}
            </div>

            <InfoBanner variant="warning" title="ReconFlow: degraded start">
              Database migration running. This service will recover automatically within ~60s. You may proceed
              to acceptance once all other services are healthy.
            </InfoBanner>
          </div>
        )}

        {/* ── DEP-03 · Acceptance Gate ───────────────────────────────── */}
        {step === "acceptance" && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex flex-col gap-1.5">
                <h1 className="text-2xl font-bold leading-[1.2] text-ink">Acceptance Gate</h1>
                <p className="text-xs text-muted">
                  {subtitle("DEP-03")} — all services ready. Verify path readiness before accepting the
                  deployment.
                </p>
              </div>
              <Button disabled={!canManage} onClick={() => canManage && acceptDeployment()}>Accept deployment →</Button>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <StatCard value="7 / 7" label="Services running" hint="All healthy" tone="success" />
              <StatCard
                value="4 / 4"
                label="Path readiness"
                hint="WalletKit · VaultCore · Mesh · Broadcast"
                tone="success"
              />
            </div>

            <div className="overflow-hidden rounded-[10px] border border-line bg-surface">
              {DEP_ACCEPTANCE_CHECKS.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center gap-3.5 border-b border-line px-4 py-3.5 last:border-b-0"
                >
                  <span
                    aria-hidden
                    className="grid size-7 shrink-0 place-items-center rounded-full bg-success-soft text-[14px] font-bold text-success"
                  >
                    ✓
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-ink">{c.name}</div>
                    <div className="mt-0.5 font-mono text-[11px] text-muted">{c.detail}</div>
                  </div>
                  <Badge variant="success">Ready</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── DEP-04 · Operate Steady-State ──────────────────────────── */}
        {step === "operate" && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex flex-col gap-1.5">
                <h1 className="text-2xl font-bold leading-[1.2] text-ink">Deployment Accepted</h1>
                <p className="text-xs text-muted">
                  {subtitle("DEP-04")} — running. All platform services are healthy.
                </p>
                <StateMachine current="operating" />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to="/wallets"
                  className={cn(buttonVariants({ variant: "primary" }), "font-medium")}
                >
                  Open Enterprise Wallet →
                </Link>
              </div>
            </div>

            <InfoBanner variant="success">
              <strong>Deployment accepted</strong> — Sep 12, 2026 09:00:00 UTC · Accepted by: Maya Okonkwo ·
              Receipt: DEP-2026-0001
            </InfoBanner>

            <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
              <div className="flex items-start justify-between gap-3 border-b border-line py-2">
                <dt className="text-xs text-muted">Deployment ID</dt>
                <dd className="font-mono text-[12px] text-ink">{DEP_OPERATE.deploymentId}</dd>
              </div>
              <div className="flex items-start justify-between gap-3 border-b border-line py-2">
                <dt className="text-xs text-muted">Accepted by</dt>
                <dd className="text-[12px] font-medium text-ink">{DEP_OPERATE.acceptedBy}</dd>
              </div>
              <div className="flex items-start justify-between gap-3 border-b border-line py-2 sm:col-span-2">
                <dt className="text-xs text-muted">Version</dt>
                <dd className="break-all font-mono text-[12px] text-ink">{DEP_OPERATE.version}</dd>
              </div>
              <div className="flex items-start justify-between gap-3 border-b border-line py-2 sm:col-span-2">
                <dt className="text-xs text-muted">Modules enabled</dt>
                <dd className="text-right text-[12px] font-medium text-ink">{DEP_OPERATE.modules}</dd>
              </div>
              <div className="flex items-start justify-between gap-3 border-b border-line py-2">
                <dt className="text-xs text-muted">Environment</dt>
                <dd className="text-[12px] font-medium text-ink">{DEP_OPERATE.environment}</dd>
              </div>
              <div className="flex items-start justify-between gap-3 border-b border-line py-2">
                <dt className="text-xs text-muted">Tenant</dt>
                <dd className="font-mono text-[12px] text-ink">{DEP_OPERATE.tenant}</dd>
              </div>
              <div className="flex items-start justify-between gap-3 border-b border-line py-2 sm:col-span-2">
                <dt className="text-xs text-muted">Accepted at</dt>
                <dd className="font-mono text-[12px] text-ink">{DEP_OPERATE.acceptedAt}</dd>
              </div>
            </dl>
          </div>
        )}

        {/* ── DEP-05 · Deployment History ────────────────────────────── */}
        {step === "history" && (
          <div className="space-y-5">
            <div className="flex flex-col gap-1.5">
              <h1 className="text-2xl font-bold leading-[1.2] text-ink">Deployment History</h1>
              <p className="text-xs text-muted">{subtitle("DEP-05")} — release log.</p>
            </div>

            <TableWrapper>
              <Table>
                <thead>
                  <tr className="border-b border-line bg-bg">
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Deployment ID</th>
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Version</th>
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Status</th>
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Accepted by</th>
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {DEP_HISTORY.map((h) => (
                    <tr key={h.deploymentId} className="border-t border-line">
                      <td className="px-4 py-3 font-mono text-[12px] text-ink">{h.deploymentId}</td>
                      <td className="px-4 py-3 font-mono text-[12px] text-text">{h.version}</td>
                      <td className="px-4 py-3">
                        <Badge variant={h.status === "active" ? "success" : "neutral"}>
                          {h.status === "active" ? "Active" : "Superseded"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-[12px] text-text">{h.acceptedBy}</td>
                      <td className="px-4 py-3 text-[12px] text-text">{h.date}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          </div>
        )}
      </div>
    </div>
  );
}